import React, { useState, useEffect } from 'react';
import { Link, Trash2 } from 'lucide-react';
import { getStoredVendors } from '../../../../utils/storage';
import type { VendorAssociation, LinkedVendor } from '@/types/vendor-association';
import { getStoredVendorAssociations, addStoredVendorAssociation, deleteStoredVendorAssociation, subscribeToAssociationUpdates } from '../../../../utils/productStorage';

interface VendorLinkSectionProps {
  productId: string;
}

export function VendorLinkSection({ productId }: VendorLinkSectionProps) {
  const [error, setError] = useState<string | null>(null);
  const [associations, setAssociations] = useState<VendorAssociation[]>(getStoredVendorAssociations());
  const vendors = getStoredVendors();
  
  const linkedVendors: LinkedVendor[] = associations
    .filter(a => a.productId === productId)
    .map(a => {
      const vendor = vendors.find(v => v.id === a.vendorId) || { name: 'Unknown Vendor' };
      return {
        vendorId: a.vendorId || '',
        vendorName: vendor?.name || 'Unknown Vendor',
        id: a.id || crypto.randomUUID(),
        preferredVendor: a.preferredVendor ?? false,
        createdAt: a.createdAt,
      };
    });
  
  const linkedVendorIds = new Set(linkedVendors.map(v => v.vendorId));

  useEffect(() => {
    return subscribeToAssociationUpdates((updatedAssociations) => {
      setAssociations(updatedAssociations);
      setError(null);
    });
  }, []);

  const handleLinkVendor = (vendorId: string) => {
    setError(null);
    if (!linkedVendorIds.has(vendorId)) {
      try {
        const association = {
          id: crypto.randomUUID(),
          vendorId,
          productId,
          preferredVendor: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        addStoredVendorAssociation(association);
      } catch (err) {
        setError('Failed to link vendor. Please try again.');
        console.error('Vendor link error:', err);
      }
    }
  };

  const handleUnlinkVendor = (vendorId: string) => {
    setError(null);
    if (window.confirm('Are you sure you want to unlink this vendor?')) {
      try {
        deleteStoredVendorAssociation(vendorId, productId);
      } catch (err) {
        setError('Failed to unlink vendor. Please try again.');
        console.error('Vendor unlink error:', err);
      }
    }
  };

  if (!productId) return null;
  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">Link Vendor</label>
        <select
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          onChange={(e) => handleLinkVendor(e.target.value)}
          value=""
        >
          <option value="">Select vendor to link</option>
          {vendors
            .filter(vendor => !linkedVendorIds.has(vendor.id))
            .map(vendor => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
        </select>
      </div>

      {linkedVendors.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Linked Vendors</h4>
          <div className="space-y-2">
            {linkedVendors.map(vendor => (
              <div
                key={vendor.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                title={`Linked on ${new Date(vendor.createdAt).toLocaleDateString()}`}
              >
                <div className="flex items-center">
                  <Link className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">{vendor.vendorName}</span>
                  {vendor.preferredVendor && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Preferred
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleUnlinkVendor(vendor.vendorId)}
                  className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}